import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CTASection = ({ onNavigate }) => {
  const ctaOptions = [
    {
      icon: "Sparkles",
      title: "Start Your Gift Journey",
      description: "Discover how AI can help you find the perfect gifts for everyone in your life.",
      action: "Find Gifts Now",
      path: "/gift-finder",
      variant: "primary"
    },
    {
      icon: "Users",
      title: "Join Our Community",
      description: "Connect with thoughtful gift-givers and share your own successful gift stories.",
      action: "Join Community",
      path: "/community",
      variant: "secondary"
    },
    {
      icon: "MessageCircle",
      title: "Get Personal Help",
      description: "Need expert advice? Our Gift Concierge service provides personalized recommendations.",
      action: "Talk to Expert",
      path: "/gift-concierge",
      variant: "outline"
    }
  ];

  const stats = [
    { number: "2M+", label: "Happy Users" },
    { number: "98%", label: "Success Rate" },
    { number: "150+", label: "Countries" },
    { number: "24/7", label: "Support" }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-primary-50 via-white to-secondary-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-32 h-32 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-secondary rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Main CTA */}
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl lg:text-5xl font-bold text-accent-800 mb-6">
            Ready to Transform Your
            <span className="text-gradient-primary block mt-2">
              Gift-Giving Experience?
            </span>
          </h2>
          <p className="text-lg lg:text-xl text-accent-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Join millions of thoughtful gift-givers who have discovered the joy of meaningful, stress-free gift-giving with Giftsy Modern.
          </p>
        </div>

        {/* CTA Options */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {ctaOptions.map((option, index) => (
            <div key={index} className="group hover-lift">
              <div className="bg-white rounded-2xl p-8 shadow-md border border-accent-100 h-full text-center transition-all duration-300 group-hover:shadow-lg group-hover:border-primary-200">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Icon name={option.icon} size={28} className="text-white" />
                </div>
                
                <h3 className="font-heading text-xl font-bold text-accent-800 mb-4">
                  {option.title}
                </h3>
                
                <p className="text-accent-600 leading-relaxed mb-6">
                  {option.description}
                </p>
                
                <Button
                  variant={option.variant}
                  fullWidth
                  onClick={() => onNavigate(option.path)}
                  className="shadow-warm hover:shadow-primary"
                >
                  {option.action}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg border border-accent-100 mb-16">
          <div className="text-center mb-8">
            <h3 className="font-heading text-2xl lg:text-3xl font-bold text-accent-800 mb-4">
              Trusted by Gift-Givers Worldwide
            </h3>
            <p className="text-lg text-accent-600">
              Join a community that values thoughtfulness and meaningful connection
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl lg:text-4xl font-bold text-accent-800 mb-2">
                  {stat.number}
                </p>
                <p className="text-accent-600 font-medium">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-8 lg:p-12 text-center text-white">
          <div className="max-w-2xl mx-auto">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-6">
              <Icon name="Mail" size={28} className="text-white" />
            </div>
            
            <h3 className="font-heading text-2xl lg:text-3xl font-bold mb-4">
              Stay Connected
            </h3>
            
            <p className="text-lg mb-8 opacity-90">
              Get the latest gift-giving tips, cultural celebration guides, and platform updates delivered to your inbox.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg text-accent-800 placeholder-accent-400 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              <Button
                variant="secondary"
                className="bg-white text-primary-600 hover:bg-accent-50 px-6"
              >
                Subscribe
              </Button>
            </div>
            
            <p className="text-sm mt-4 opacity-75">
              No spam, unsubscribe anytime. We respect your privacy.
            </p>
          </div>
        </div>

        {/* Contact Information */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-accent-100 inline-block">
            <h4 className="font-heading text-lg font-bold text-accent-800 mb-4">
              Have Questions?
            </h4>
            <p className="text-accent-600 mb-6">
              We'd love to hear from you. Get in touch with our team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="outline"
                iconName="Mail"
                iconPosition="left"
              >
                hello@giftsy.com
              </Button>
              <Button
                variant="outline"
                iconName="MessageCircle"
                iconPosition="left"
                onClick={() => onNavigate('/gift-concierge')}
              >
                Live Chat
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;